/**
 * Pure use-phase carbon math (SIL-340, T2 of
 * resources/plans/use-phase-carbon.md §Calculation / §PUE / §Bounds).
 *
 * No DB access. `apps/ingestion`'s CLI (SIL-343) calls BoaviztAPI for the
 * authoritative `gwp.use` figure; the local `useAnnualKgco2e` here exists so
 * that call can be cross-checked against an independently-derived number,
 * and so `applyPue` / the bounds helpers below have one shared home used by
 * every Scope-2-labeled surface (asset detail, ESG CSV).
 */

const round3 = (n: number): number => Math.round(n * 1000) / 1000;

/** Hours in a year — same annualization constant as power-cost.ts's HOURS_PER_YEAR. */
export const USE_PHASE_HOURS_PER_YEAR = 8760;

// ---------------------------------------------------------------------------
// PUE bound — server-side range for any USER-SUPPLIED PUE (e.g.
// `financial_scenario.pue`), plan §PUE: "Bound user-supplied PUE server-side
// to [1.0, 3.0] and reject out-of-range rather than clamp." `applyPue`
// itself does not enforce this range (its only contract is "throw on
// null") — a caller that accepts a PUE value FROM A REQUEST must validate
// it against this range BEFORE the value ever reaches `applyPue`, and must
// reject (400), never silently clamp into range, since a clamp would still
// let an absurd input (e.g. 50) quietly become a plausible-looking 3.0.
// ---------------------------------------------------------------------------

export const USE_PHASE_PUE_MIN = 1.0;
export const USE_PHASE_PUE_MAX = 3.0;

/** @pure */
export function isPueInAllowedRange(pue: number): boolean {
  return pue >= USE_PHASE_PUE_MIN && pue <= USE_PHASE_PUE_MAX;
}

// ---------------------------------------------------------------------------
// applyPue — the ONE shared implementation every Scope-2-labeled surface calls
// ---------------------------------------------------------------------------

/**
 * Applies PUE to a device-level annual-rate figure to produce the
 * purchased-electricity (Scope 2) figure: `rate * pue`. The stored
 * `asset_use_phase_snapshot` primitive is device-level (PUE belongs to a
 * facility, not an asset — see 707's column comment); Scope 2 means
 * purchased electricity, which includes cooling and distribution overhead,
 * so a device-level number rendered under a Scope 2 label is systematically
 * ~1.3-1.6x low.
 *
 * **Contract for `pue = null`: throws.** This is a deliberate departure from
 * `power-cost.ts`'s `annualPowerCostUsd`, which silently treats `pue ?? 1`
 * (a documented-elsewhere financial-scenario convenience, NOT a pattern to
 * copy here — see the plan's "Never" list / SIL-351). For a Scope-2 label,
 * PUE is supposed to always be resolved by the caller's ladder
 * (customer-supplied -> org-supplied -> published industry default) before
 * this function is ever called; a published default is always available, so
 * a `null` reaching `applyPue` means the caller skipped the ladder, not that
 * "no PUE" is a legitimate state. Silently defaulting to 1.0 here would
 * reproduce exactly the ~1.3-1.6x understatement bug this function exists to
 * prevent, invisibly. Callers that legitimately don't have a resolved PUE
 * yet must resolve one (even to a named published default) before calling.
 *
 * @pure
 * @throws {Error} if `pue` is `null` — resolve PUE before calling, never pass null.
 */
export function applyPue(deviceRateKgco2e: number, pue: number | null): number {
  if (pue === null) {
    throw new Error(
      'applyPue: pue must be resolved (customer-supplied -> org-supplied -> ' +
        'published industry default) before calling — a null pue is a caller ' +
        'bug, not a silent 1.0 factor. Device-level != Scope 2.',
    );
  }
  return round3(deviceRateKgco2e * pue);
}

// ---------------------------------------------------------------------------
// useAnnualKgco2e — local cross-check arithmetic (SIL-343 reconciles against
// this; BoaviztAPI's gwp.use remains the authoritative stored figure)
// ---------------------------------------------------------------------------

/**
 * `kgCO2e/yr = (watts / 1000) * hours/yr * useTimeRatio * (gridFactor g/kWh) / 1000`
 *
 * Device-level (no PUE applied — see `applyPue`). `useTimeRatio` is
 * `total_uptime_seconds / window_seconds`, where the denominator is calendar
 * days in the window (not days-with-a-row — see the plan's telemetry-keying
 * note on why that distinction matters for coverage).
 *
 * @pure
 */
export function useAnnualKgco2e(
  watts: number,
  useTimeRatio: number,
  gridFactorGPerKwh: number,
): number {
  const kwPerYear = (watts / 1000) * USE_PHASE_HOURS_PER_YEAR * useTimeRatio;
  const gramsPerYear = kwPerYear * gridFactorGPerKwh;
  return round3(gramsPerYear / 1000);
}

// ---------------------------------------------------------------------------
// Bounds policy (measured -> observed_p10_p90, archetype -> archetype_span)
// ---------------------------------------------------------------------------

export type BoundsBasis = 'observed_p10_p90' | 'archetype_span';

/**
 * Minimum non-null daily samples required before a p10/p90 is reported as a
 * defensible empirical interval. Below this, `observedPowerPercentiles`
 * returns `null` rather than a collapsed/degenerate band — a 1-element
 * series has no distribution to speak of, and reporting `p10 === p90` for
 * it would present false precision under the exact same
 * "best-instrumented asset looks artificially certain" failure mode the
 * plan calls out for the archetype path. This is a DIFFERENT, stricter gate
 * than the tier-qualification coverage threshold ("≥1 full calendar month,
 * ≥80% daily coverage") — that gate decides whether `power_basis` is
 * `telemetry_measured` at all; this one decides whether the resulting
 * sample is large enough to support a percentile claim once it is.
 */
export const MIN_OBSERVED_SAMPLES_FOR_BOUNDS = 5;

export interface ObservedPowerBounds {
  p10Watts: number;
  p90Watts: number;
  /** Non-null samples actually used (NULL days are dropped, not zero-filled). */
  sampleCount: number;
}

/**
 * p10/p90 of observed daily `avg_power_draw_watts` across the window
 * (`bounds_basis = 'observed_p10_p90'`), linear-interpolated (the standard
 * "inclusive"/R-7 method). NULL days are dropped before computing the
 * percentiles — a day with no telemetry row/aggregate contributes no power
 * observation, it is not a zero draw.
 *
 * **Insufficient series (`< MIN_OBSERVED_SAMPLES_FOR_BOUNDS` non-null
 * values, including the empty array): returns `null`.** Callers on the
 * measured path MUST treat a `null` result as "cannot compute a defensible
 * observed_p10_p90 band" — never substitute a single point, a zero-width
 * band, or fall back silently to a narrower/synthetic range. What to do
 * instead (fall back to the archetype path for that asset, or report the
 * gap) is a caller/CLI (SIL-343) decision, not this function's.
 *
 * @pure
 */
export function observedPowerPercentiles(
  dailyAvgWattsSeries: ReadonlyArray<number | null | undefined>,
): ObservedPowerBounds | null {
  const samples = dailyAvgWattsSeries.filter(
    (w): w is number => typeof w === 'number' && Number.isFinite(w),
  );
  if (samples.length < MIN_OBSERVED_SAMPLES_FOR_BOUNDS) return null;

  const sorted = [...samples].sort((a, b) => a - b);
  return {
    p10Watts: round3(percentile(sorted, 0.1)),
    p90Watts: round3(percentile(sorted, 0.9)),
    sampleCount: samples.length,
  };
}

/** Linear-interpolated percentile over an ascending-sorted, non-empty array. */
function percentile(sortedAsc: readonly number[], p: number): number {
  const first = sortedAsc[0];
  if (first === undefined) {
    // Unreachable given callers always gate on length >= MIN_OBSERVED_SAMPLES_FOR_BOUNDS,
    // but keeps this function honest under noUncheckedIndexedAccess.
    throw new Error('percentile: sortedAsc must be non-empty');
  }
  if (sortedAsc.length === 1) return first;

  const idx = p * (sortedAsc.length - 1);
  const lo = Math.floor(idx);
  const hi = Math.ceil(idx);
  const loVal = sortedAsc[lo];
  const hiVal = sortedAsc[hi];
  if (loVal === undefined || hiVal === undefined) {
    throw new Error('percentile: index out of bounds');
  }
  if (lo === hi) return loVal;
  const frac = idx - lo;
  return loVal + (hiVal - loVal) * frac;
}

export interface UsePhaseAnnualBounds {
  minKgco2e: number;
  centralKgco2e: number;
  maxKgco2e: number;
  boundsBasis: BoundsBasis;
}

/**
 * Composes an observed p10/p90 watts band with the resolved grid factor and
 * use-time ratio into an annual kgCO2e min/central/max — the
 * `observed_p10_p90` bounds path. PUE is deliberately NOT applied here (the
 * stored figures are device-level; see `applyPue`).
 *
 * "Composed with grid-factor and PUE uncertainty" (plan §Bounds) is only
 * partially modeled by this function: it propagates POWER uncertainty
 * (p10/p90 watts) through the same grid factor used for the central
 * estimate. It does NOT widen the band for grid-factor or PUE uncertainty
 * themselves — no numeric uncertainty distribution for either input is
 * specified anywhere in the plan or this ticket. Flagged as underspecified;
 * a follow-up (or SIL-343, which has the real Boavizta figures to reconcile
 * against) should decide whether/how to fold those in.
 *
 * @pure
 */
export function useAnnualKgco2eBoundsFromObserved(
  p10Watts: number,
  centralWatts: number,
  p90Watts: number,
  useTimeRatio: number,
  gridFactorGPerKwh: number,
): UsePhaseAnnualBounds {
  return {
    minKgco2e: useAnnualKgco2e(p10Watts, useTimeRatio, gridFactorGPerKwh),
    centralKgco2e: useAnnualKgco2e(centralWatts, useTimeRatio, gridFactorGPerKwh),
    maxKgco2e: useAnnualKgco2e(p90Watts, useTimeRatio, gridFactorGPerKwh),
    boundsBasis: 'observed_p10_p90',
  };
}

/**
 * Archetype-path bounds: Boavizta's own returned min/max/central, passed
 * through unchanged (`bounds_basis = 'archetype_span'`). This exists purely
 * so callers have one symmetric entry point per bounds basis instead of
 * hand-rolling the tagged object at each call site.
 *
 * @pure
 */
export function useAnnualKgco2eBoundsFromArchetype(
  minKgco2e: number,
  centralKgco2e: number,
  maxKgco2e: number,
): UsePhaseAnnualBounds {
  return {
    minKgco2e,
    centralKgco2e,
    maxKgco2e,
    boundsBasis: 'archetype_span',
  };
}

// ---------------------------------------------------------------------------
// Reconciliation tolerances (SIL-380) — the FIRST CALIBRATED constants in
// this module. Every other threshold above (`MIN_MEASURED_POWER_WINDOW_DAYS`,
// `MIN_MEASURED_POWER_COVERAGE_RATIO` in accuracy-tier.ts,
// `MIN_OBSERVED_SAMPLES_FOR_BOUNDS` above) documents itself as "reasoned, not
// calibrated" — there was no real data to calibrate any of them against at
// the time they were written. These two ARE calibrated, against real data:
// calibrated 2026-07-30 against all 77 production `asset_use_phase_snapshot`
// rows for period 2026-06. Measured path (`power_basis =
// 'telemetry_measured'`): p50 relative delta 0.006%, max 0.024% (n=58).
// Archetype path (`power_basis = 'archetype_default'`): p50 0.888%, max
// 0.889% (n=19).
// ---------------------------------------------------------------------------

/**
 * The entire measured/archetype residual above is BoaviztAPI OUTPUT
 * ROUNDING, not a modeling disagreement: BoaviztAPI's own `gwp.use` figure
 * carries 4 significant figures on the measured path and only 2 significant
 * figures on the archetype path. At the ~1900 kg scale typical of the
 * archetype-path population, a 2-s.f. rounding step is 100 kg — rounding
 * ALONE can therefore reach a 2.6% relative delta on an otherwise perfectly
 * healthy archetype-path asset. A single flat threshold (e.g. a naive 2%)
 * would false-positive on exactly those healthy assets, which is why the
 * two paths below get different tolerances keyed off `power_basis` rather
 * than one shared constant.
 *
 * Relative delta = `abs(useAnnualKgco2e(...) - gwp_use_annual_kgco2eq) /
 * gwp_use_annual_kgco2eq`, where `useAnnualKgco2e(...)` is OUR independent
 * cross-check arithmetic and `gwp_use_annual_kgco2eq` (the stored column) is
 * BoaviztAPI's authoritative figure. A relative delta exceeding the
 * tolerance for that snapshot's `power_basis` should be flagged (amber), not
 * silently accepted or hard-failed.
 */
export const USE_PHASE_RECONCILIATION_TOLERANCE_MEASURED = 0.005;

/**
 * See `USE_PHASE_RECONCILIATION_TOLERANCE_MEASURED`'s docstring for the full
 * calibration rationale — this is the archetype-path counterpart, set 6x
 * looser (0.03 vs. 0.005) specifically because BoaviztAPI's archetype-path
 * `gwp.use` figure is coarser (2 significant figures vs. 4 on the measured
 * path), not because the archetype path is modeled less rigorously.
 */
export const USE_PHASE_RECONCILIATION_TOLERANCE_ARCHETYPE = 0.03;

/**
 * Reconciles our independently-derived `useAnnualKgco2e(...)` against the
 * stored, authoritative `gwp_use_annual_kgco2eq` for one snapshot.
 *
 * **Contract on `useTimeRatio = null`:** returns `{ status:
 * 'cannot_reconcile', ... }` rather than computing (and rendering) `NaN`.
 * `use_time_ratio` is read from `asset_use_phase_snapshot.input_payload`,
 * which is NOT validated by `usePhaseInputProvenanceSchema` (that schema
 * covers `input_provenance`, a different column) — an older row predating
 * this field, or a row whose payload shape drifted, must degrade visibly,
 * never silently.
 *
 * @pure
 */
export function reconcileUseAnnualKgco2e(params: {
  watts: number | null;
  useTimeRatio: number | null;
  gridFactorGPerKwh: number | null;
  storedGwpUseAnnualKgco2eq: number;
  powerBasis: 'telemetry_measured' | 'archetype_default';
}): UsePhaseReconciliation {
  if (params.useTimeRatio === null) {
    return {
      status: 'cannot_reconcile',
      reason: 'cannot reconcile — use_time_ratio absent from input_payload',
    };
  }
  if (params.watts === null || params.gridFactorGPerKwh === null) {
    return {
      status: 'cannot_reconcile',
      reason: 'cannot reconcile — resolved_watts or grid_intensity_g_per_kwh absent from input_payload',
    };
  }

  const independentKgco2e = useAnnualKgco2e(
    params.watts,
    params.useTimeRatio,
    params.gridFactorGPerKwh,
  );
  const toleranceApplied =
    params.powerBasis === 'telemetry_measured'
      ? USE_PHASE_RECONCILIATION_TOLERANCE_MEASURED
      : USE_PHASE_RECONCILIATION_TOLERANCE_ARCHETYPE;
  const relativeDeltaAbs =
    params.storedGwpUseAnnualKgco2eq === 0
      ? independentKgco2e === 0
        ? 0
        : 1
      : Math.abs(independentKgco2e - params.storedGwpUseAnnualKgco2eq) /
        params.storedGwpUseAnnualKgco2eq;

  return {
    status: 'reconciled',
    independentKgco2e,
    storedKgco2e: params.storedGwpUseAnnualKgco2eq,
    relativeDeltaAbs,
    toleranceApplied,
    flagged: relativeDeltaAbs > toleranceApplied,
  };
}

export type UsePhaseReconciliation =
  | {
      status: 'reconciled';
      independentKgco2e: number;
      storedKgco2e: number;
      relativeDeltaAbs: number;
      toleranceApplied: number;
      /** `true` when `relativeDeltaAbs` exceeds the tolerance for this snapshot's `powerBasis`. */
      flagged: boolean;
    }
  | {
      status: 'cannot_reconcile';
      reason: string;
    };
